/**
 * Lumen Row — storefront behaviour.
 *
 * Replaces, in vanilla JS, exactly the behaviours the Hydrogen storefront gets
 * from React + Radix Dialog. Nothing more. No framework, no bundler.
 *
 *   LrDrawer          focus trap, return focus, inert background, scroll lock
 *   header            hide on scroll down / return on scroll up (transform only)
 *   nav disclosure    button + aria-expanded panel, Escape closes, click-away
 *   gallery           variant-driven frame, thumb rail, snap carousel + dots
 *   variant picker    ?variant= navigation, preserving Hydrogen's URL semantics
 *   cart              AJAX add/change with Section Rendering for the drawer
 *
 * Motion is transform/opacity only. The global reduced-motion rule in
 * lr-base.css collapses durations; nothing here re-implements that.
 */
(function () {
  'use strict';

  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  /* ===================================================================
   * DRAWER
   *
   * The Hydrogen shell uses Radix Dialog for four guarantees the skeleton
   * lacked: focus trap, return focus to the trigger, background inertness,
   * and scroll lock without a layout jump. All four are reproduced here.
   *
   * Scroll lock compensates for the scrollbar width so the page does not
   * shift horizontally when the drawer opens — the "no layout jump" part.
   * ================================================================ */
  var openDrawers = [];

  function lockScroll() {
    if (openDrawers.length > 1) return;
    var gap = window.innerWidth - document.documentElement.clientWidth;
    if (gap > 0) document.body.style.paddingRight = gap + 'px';
    document.body.classList.add('lr-scroll-locked');
  }

  function unlockScroll() {
    if (openDrawers.length) return;
    document.body.classList.remove('lr-scroll-locked');
    document.body.style.paddingRight = '';
  }

  function LrDrawer(root) {
    this.root = root;
    this.panel = root.querySelector('.lr-drawer__panel');
    this.overlay = root.querySelector('.lr-drawer__overlay');
    this.restore = null;
    this.onKeydown = this.handleKeydown.bind(this);

    var self = this;
    if (this.overlay) {
      this.overlay.addEventListener('click', function () {
        self.close();
      });
    }
    root.querySelectorAll('[data-drawer-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        self.close();
      });
    });
  }

  LrDrawer.prototype.isOpen = function () {
    return this.root.getAttribute('data-state') === 'open';
  };

  LrDrawer.prototype.open = function () {
    if (this.isOpen()) return;
    // Captured before anything moves focus — this is the trigger.
    this.restore = document.activeElement;
    this.root.hidden = false;
    this.root.setAttribute('data-state', 'open');
    openDrawers.push(this);
    lockScroll();
    setInert(true);
    document.addEventListener('keydown', this.onKeydown);

    var first = this.panel.querySelector(FOCUSABLE);
    (first || this.panel).focus({preventScroll: true});
    this.root.dispatchEvent(new CustomEvent('lr:drawer:open', {bubbles: true}));
  };

  LrDrawer.prototype.close = function () {
    if (!this.isOpen()) return;
    this.root.setAttribute('data-state', 'closed');
    this.root.hidden = true;
    openDrawers = openDrawers.filter(function (d) {
      return d !== this;
    }, this);
    document.removeEventListener('keydown', this.onKeydown);
    if (!openDrawers.length) setInert(false);
    unlockScroll();

    // Explicit restoration. Radix was observed returning focus to <body>;
    // losing focus to the body means the next Tab starts from the document
    // top, which is a real keyboard regression.
    var el = this.restore;
    if (el && document.body.contains(el)) el.focus({preventScroll: true});
    this.restore = null;
    this.root.dispatchEvent(new CustomEvent('lr:drawer:close', {bubbles: true}));
  };

  LrDrawer.prototype.handleKeydown = function (event) {
    if (openDrawers[openDrawers.length - 1] !== this) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (event.key !== 'Tab') return;

    var items = Array.prototype.filter.call(
      this.panel.querySelectorAll(FOCUSABLE),
      function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      }
    );
    if (!items.length) {
      event.preventDefault();
      this.panel.focus();
      return;
    }
    var first = items[0];
    var last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /** Background inertness — the `aria-hidden` half of what Radix supplies. */
  function setInert(on) {
    ['.lr-header', '#main', '.lr-footer', '.lr-announcement'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (!el) return;
      if (on) {
        el.setAttribute('aria-hidden', 'true');
        el.setAttribute('inert', '');
      } else {
        el.removeAttribute('aria-hidden');
        el.removeAttribute('inert');
      }
    });
  }

  var drawers = {};

  function getDrawer(name) {
    if (drawers[name]) return drawers[name];
    var root = document.querySelector('[data-drawer="' + name + '"]');
    if (!root) return null;
    drawers[name] = new LrDrawer(root);
    return drawers[name];
  }

  function initDrawers() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-drawer-open]');
      if (!trigger) return;
      var name = trigger.getAttribute('data-drawer-open');
      var drawer = getDrawer(name);
      if (!drawer) return;
      event.preventDefault();
      drawer.open();
      if (name === 'search') {
        var input = drawer.panel.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    });
  }

  /* ===================================================================
   * HEADER — hide on scroll down, return on scroll up.
   *
   * Deliberately conservative, matching the Hydrogen hook exactly:
   *   12px delta threshold, so a trackpad twitch cannot flicker it
   *   never hidden above 96px, so the top of the page always shows it
   *   re-shown on focusin, so a keyboard user cannot tab into a hidden header
   * ================================================================ */
  function initHeaderScroll() {
    var header = document.querySelector('.lr-header');
    if (!header) return;
    var threshold = 12;
    var floor = 96;
    var lastY = window.scrollY;

    window.addEventListener(
      'scroll',
      function () {
        var y = window.scrollY;
        var delta = y - lastY;
        if (Math.abs(delta) < threshold) return;
        if (y > floor && delta > 0) header.setAttribute('data-hidden', '');
        else header.removeAttribute('data-hidden');
        lastY = y;
      },
      {passive: true}
    );

    window.addEventListener('focusin', function () {
      header.removeAttribute('data-hidden');
    });
  }

  /* ===================================================================
   * NAV DISCLOSURE
   *
   * A disclosure, not a menu widget: a real <button aria-expanded> toggling
   * a panel of ordinary links. Menu semantics take links out of the tab
   * order and impose arrow-key traversal nobody expects on a nav bar.
   * ================================================================ */
  function initNavDisclosure() {
    var wraps = document.querySelectorAll('.lr-nav__disclosure');
    if (!wraps.length) return;

    function closeAll(except) {
      wraps.forEach(function (wrap) {
        if (wrap === except) return;
        var btn = wrap.querySelector('[aria-expanded]');
        var panel = wrap.querySelector('.lr-nav__panel');
        if (btn) btn.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
    }

    wraps.forEach(function (wrap) {
      var btn = wrap.querySelector('[aria-expanded]');
      var panel = wrap.querySelector('.lr-nav__panel');
      if (!btn || !panel) return;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        closeAll(wrap);
        btn.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var open = document.querySelector('.lr-nav__disclosure [aria-expanded="true"]');
      if (!open) return;
      closeAll(null);
      open.focus();
    });

    ['pointerdown', 'focusin'].forEach(function (type) {
      document.addEventListener(type, function (event) {
        if (event.target.closest && event.target.closest('.lr-nav__disclosure')) return;
        closeAll(null);
      });
    });
  }

  /* ===================================================================
   * PRODUCT GALLERY
   *
   * Desktop is a main frame plus a thumbnail rail; mobile is a scroll-snap
   * carousel with dots. Both render the same frame list, so the hidden
   * presentation is a cache hit rather than a second download.
   * ================================================================ */
  function initGallery(scope) {
    var gallery = (scope || document).querySelector('[data-gallery]');
    if (!gallery) return;

    var frames = gallery.querySelectorAll('[data-gallery-frame]');
    var thumbs = gallery.querySelectorAll('[data-gallery-thumb]');
    var dots = gallery.querySelectorAll('[data-gallery-dot]');
    var scroller = gallery.querySelector('[data-gallery-scroller]');
    var status = gallery.querySelector('[data-gallery-status]');
    var total = frames.length;
    if (!total) return;

    function show(index, scroll) {
      var next = ((index % total) + total) % total;
      frames.forEach(function (frame, i) {
        if (i === next) {
          frame.setAttribute('data-active', '');
          frame.removeAttribute('aria-hidden');
        } else {
          frame.removeAttribute('data-active');
          frame.setAttribute('aria-hidden', 'true');
        }
      });
      thumbs.forEach(function (thumb, i) {
        if (i === next) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      dots.forEach(function (dot, i) {
        if (i === next) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (status) status.textContent = 'Image ' + (next + 1) + ' of ' + total;
      gallery.setAttribute('data-index', String(next));
      if (scroll && scroller) {
        scroller.scrollTo({left: scroller.clientWidth * next, behavior: 'smooth'});
      }
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () {
        show(i, true);
      });
    });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () {
        show(i, true);
      });
    });
    gallery.querySelectorAll('[data-gallery-prev]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        show(Number(gallery.getAttribute('data-index') || 0) - 1, true);
      });
    });
    gallery.querySelectorAll('[data-gallery-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        show(Number(gallery.getAttribute('data-index') || 0) + 1, true);
      });
    });

    // Keep the dots honest while the shopper swipes.
    if (scroller) {
      var raf = null;
      scroller.addEventListener(
        'scroll',
        function () {
          if (raf) return;
          raf = requestAnimationFrame(function () {
            raf = null;
            if (!scroller.clientWidth) return;
            show(Math.round(scroller.scrollLeft / scroller.clientWidth), false);
          });
        },
        {passive: true}
      );
    }

    // The variant drives the frame: jump to the media id the picker names.
    gallery.addEventListener('lr:variant', function (event) {
      var mediaId = event.detail && event.detail.mediaId;
      if (!mediaId) return;
      for (var i = 0; i < frames.length; i += 1) {
        if (frames[i].getAttribute('data-media-id') === String(mediaId)) {
          show(i, true);
          return;
        }
      }
    });

    show(Number(gallery.getAttribute('data-index') || 0), false);
  }

  /* ===================================================================
   * VARIANT PICKER
   *
   * Same URL semantics Hydrogen preserves: selecting a value replaces the
   * history entry rather than pushing one, and never scrolls. Liquid has
   * already rendered every option value with the variant it resolves to,
   * so no client-side matching matrix is needed.
   * ================================================================ */
  function initVariantPicker() {
    var form = document.querySelector('[data-variant-picker]');
    if (!form) return;
    var section = form.closest('[data-product-root]') || document;

    form.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-variant-id]');
      if (!btn || btn.disabled) return;
      event.preventDefault();
      var id = btn.getAttribute('data-variant-id');
      if (!id) return;
      applyVariant(section, id, btn);
    });
  }

  function applyVariant(section, variantId, trigger) {
    var data = section.querySelector('[data-variants-json]');
    if (!data) return;
    var variants = JSON.parse(data.textContent);
    var variant = variants.filter(function (v) {
      return String(v.id) === String(variantId);
    })[0];
    if (!variant) return;

    // URL first: the variant is a linkable state, exactly as on Hydrogen.
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // Chips
    section.querySelectorAll('[data-variant-id]').forEach(function (el) {
      el.setAttribute('aria-pressed', 'false');
    });
    section.querySelectorAll('[data-option-position]').forEach(function (group) {
      var position = Number(group.getAttribute('data-option-position'));
      var value = variant.options[position - 1];
      group.querySelectorAll('[data-option-value]').forEach(function (el) {
        var match = el.getAttribute('data-option-value') === value;
        el.setAttribute('aria-pressed', match ? 'true' : 'false');
      });
    });
    if (trigger) trigger.setAttribute('aria-pressed', 'true');

    // Price
    var price = section.querySelector('[data-variant-price]');
    if (price) price.innerHTML = variant.price_html;
    var compare = section.querySelector('[data-variant-compare]');
    if (compare) {
      compare.innerHTML = variant.compare_at_price_html || '';
      compare.hidden = !variant.compare_at_price_html;
    }

    // Availability
    var submit = section.querySelector('[data-add-to-cart]');
    if (submit) {
      submit.disabled = !variant.available;
      var label = submit.querySelector('.lr-btn__label');
      if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
    }
    var idInput = section.querySelector('[data-variant-input]');
    if (idInput) idInput.value = variant.id;

    // Gallery follows the variant.
    var gallery = section.querySelector('[data-gallery]');
    if (gallery && variant.featured_media_id) {
      gallery.dispatchEvent(
        new CustomEvent('lr:variant', {detail: {mediaId: variant.featured_media_id}})
      );
    }
  }

  /* ===================================================================
   * QUANTITY STEPPER (pre-add, PDP)
   *
   * NO ARTIFICIAL CEILING. This catalogue exposes no per-variant inventory
   * ceiling to the storefront, so inventing one would either block a
   * legitimate order or imply stock information we do not have.
   * ================================================================ */
  function initStepper() {
    document.querySelectorAll('[data-stepper]').forEach(function (stepper) {
      var input = stepper.querySelector('[data-stepper-input]');
      var output = stepper.querySelector('[data-stepper-value]');
      var minus = stepper.querySelector('[data-stepper-minus]');
      var plus = stepper.querySelector('[data-stepper-plus]');
      if (!input) return;

      function set(next) {
        var value = Math.max(1, next);
        input.value = String(value);
        if (output) output.textContent = String(value);
        if (minus) minus.disabled = value <= 1;
      }
      if (minus) minus.addEventListener('click', function () { set(Number(input.value) - 1); });
      if (plus) plus.addEventListener('click', function () { set(Number(input.value) + 1); });
      set(Number(input.value) || 1);
    });
  }

  /* ===================================================================
   * CART
   *
   * AJAX Cart API plus the Section Rendering API, so the drawer and the
   * header count come back as rendered Liquid rather than being rebuilt in
   * JS from a JSON cart. Checkout is untouched — the checkout button is a
   * plain submit to /checkout, which is Shopify Checkout.
   * ================================================================ */
  function cartSectionsToRender() {
    return ['lr-cart-drawer', 'lr-cart-count'].join(',');
  }

  function replaceSection(html, id, selector) {
    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var incoming = parsed.querySelector(selector);
    var current = document.querySelector(selector);
    if (incoming && current) current.innerHTML = incoming.innerHTML;
  }

  function refreshCartSections(sections) {
    if (!sections) return;
    if (sections['lr-cart-drawer']) {
      replaceSection(sections['lr-cart-drawer'], 'lr-cart-drawer', '[data-cart-drawer-body]');
    }
    if (sections['lr-cart-count']) {
      replaceSection(sections['lr-cart-count'], 'lr-cart-count', '[data-cart-count-root]');
    }
    document.dispatchEvent(new CustomEvent('lr:cart:updated'));
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify(body),
    }).then(function (res) {
      return res.json().then(function (json) {
        if (!res.ok) throw new Error(json.description || json.message || 'Cart error');
        return json;
      });
    });
  }

  function initAddToCart() {
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-cart-add-form]');
      if (!form) return;
      event.preventDefault();

      var submit = form.querySelector('[data-add-to-cart]');
      var errorEl = form.querySelector('[data-cart-error]');
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
      if (submit) submit.setAttribute('aria-busy', 'true');

      var body = {
        items: [
          {
            id: Number(form.querySelector('[data-variant-input]').value),
            quantity: Number((form.querySelector('[data-stepper-input]') || {}).value || 1),
          },
        ],
        sections: cartSectionsToRender(),
      };

      postJSON('/cart/add.js', body)
        .then(function (json) {
          refreshCartSections(json.sections);
          var drawer = getDrawer('cart');
          if (drawer) drawer.open();
        })
        .catch(function (error) {
          if (errorEl) { errorEl.textContent = error.message; errorEl.hidden = false; }
        })
        .then(function () {
          if (submit) submit.removeAttribute('aria-busy');
        });
    });
  }

  function changeLine(key, quantity, origin) {
    if (origin) origin.classList.add('lr-stepper--busy');
    return postJSON('/cart/change.js', {
      id: key,
      quantity: quantity,
      sections: cartSectionsToRender(),
    })
      .then(function (json) {
        refreshCartSections(json.sections);
        // The /cart page is server-rendered, so reload it rather than
        // hand-patching two representations of the same cart out of sync.
        if (window.location.pathname.replace(/\/$/, '').endsWith('/cart')) {
          window.location.reload();
        }
      })
      .catch(function () {
        window.location.reload();
      });
  }

  function initCartMutations() {
    document.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        event.preventDefault();
        changeLine(remove.getAttribute('data-cart-remove'), 0, null);
        return;
      }
      var step = event.target.closest('[data-cart-step]');
      if (!step) return;
      event.preventDefault();
      changeLine(
        step.getAttribute('data-line-key'),
        Number(step.getAttribute('data-cart-step')),
        step.closest('.lr-stepper')
      );
    });
  }

  /* =================================================================== */
  function init() {
    initDrawers();
    initHeaderScroll();
    initNavDisclosure();
    initGallery(document);
    initVariantPicker();
    initStepper();
    initAddToCart();
    initCartMutations();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The Theme Editor re-renders a section in place; re-bind what it replaced.
  document.addEventListener('shopify:section:load', function () {
    initGallery(document);
    initVariantPicker();
    initStepper();
    initNavDisclosure();
  });
  document.addEventListener('lr:cart:updated', function () {
    initStepper();
  });
})();
